import type {
  CrmLinkStatus,
  CrmMappedField,
  CrmSourceField,
  CrmSyncEvent,
  CrmSyncRunOutcome,
} from '@platform/contracts';
import type { BadgeTone } from '@platform/ui';

/**
 * The words the Marketing sync tab uses, and the rules it checks before it asks the API
 * (MASTER_PLAN M3.12, FR-CRM-06, `INTEGRATIONS.md` §9A).
 *
 * Kept out of the components for the same reason `status.ts` is: one vocabulary for the
 * mapping form and the patient panel, testable without rendering anything.
 *
 * Every label is the clinic's word rather than the contract's. `no_show` is "Did not
 * show", `skipped_not_attested` is "Skipped: HIPAA mode not confirmed" — a clinic manager
 * should be able to read a run log without a glossary, and a reason that has to be
 * translated is a reason nobody acts on.
 */

/* ------------------------------------------------------------------- events */

export const CRM_EVENT_LABELS: Record<CrmSyncEvent, string> = {
  booked: 'Booked',
  rescheduled: 'Rescheduled',
  cancelled: 'Cancelled',
  completed: 'Completed',
  no_show: 'Did not show',
};

export const CRM_EVENT_HINTS: Record<CrmSyncEvent, string> = {
  booked: 'Applied when a patient books a visit.',
  rescheduled: 'Applied when a booked visit moves.',
  cancelled: 'Applied when a visit is cancelled.',
  completed: 'Applied after the visit happened.',
  no_show: 'Applied when the patient did not attend.',
};

/* ------------------------------------------------------------------- fields */

/** The four CRM custom fields a mapping may fill. The destinations are fixed. */
export const CRM_MAPPED_FIELD_LABELS: Record<CrmMappedField, string> = {
  appointment_type_key: 'Visit type',
  next_appointment_date: 'Next appointment date',
  last_appointment_status: 'Last appointment status',
  booking_source: 'Booking source',
};

/** The platform values a CRM field may be filled from. This is the allow-list. */
export const CRM_SOURCE_LABELS: Record<CrmSourceField, string> = {
  none: 'Send nothing',
  appointment_type_key: 'Visit type key',
  next_appointment_at: 'Next appointment date',
  appointment_status: 'Last appointment status',
  booking_source: 'Booking source',
};

/* -------------------------------------------------------------- patient panel */

export const CRM_LINK_STATUS_LABELS: Record<CrmLinkStatus, string> = {
  linked: 'In HighLevel',
  pending: 'Waiting to sync',
  failed: 'Last sync failed',
  unlinked: 'Removed in HighLevel',
};

export const CRM_LINK_STATUS_TONES: Record<CrmLinkStatus, BadgeTone> = {
  linked: 'success',
  pending: 'info',
  failed: 'danger',
  unlinked: 'neutral',
};

export const CRM_OUTCOME_LABELS: Record<CrmSyncRunOutcome, string> = {
  synced: 'Synced',
  unchanged: 'Nothing had changed',
  skipped_no_install: 'Skipped: HighLevel is not connected',
  skipped_no_consent: 'Skipped: no marketing authorization',
  skipped_not_attested: 'Skipped: HIPAA mode not confirmed',
  failed: 'Failed',
  // Booking-funnel leads (ADR-037).
  skipped_leads_off: 'Skipped: booking leads are turned off',
  skipped_existing_patient: 'Skipped: already a patient',
  skipped_no_lead: 'Skipped: no lead to send',
};

export const CRM_OUTCOME_TONES: Record<CrmSyncRunOutcome, BadgeTone> = {
  synced: 'success',
  unchanged: 'neutral',
  skipped_no_install: 'neutral',
  skipped_no_consent: 'warning',
  skipped_not_attested: 'warning',
  failed: 'danger',
  skipped_leads_off: 'neutral',
  skipped_existing_patient: 'neutral',
  skipped_no_lead: 'neutral',
};

/** What travelled. `minimal` is the operational set only: no marketing field left. */
export const CRM_MODE_LABELS: Record<'full' | 'minimal' | 'none', string> = {
  full: 'Marketing and appointment fields',
  minimal: 'Appointment fields only',
  none: 'Nothing was sent',
};

/* -------------------------------------------------------------- attestation */

/**
 * The attestation, in the clinic's own voice.
 *
 * Composed from `INTEGRATIONS.md` §9A ("Required permissions and accounts" and "BAA
 * status"), which states the three facts but states them *about* the clinic; a checkbox
 * has to be something the person ticking it is saying. Nothing is added: the HIPAA add-on,
 * the in-app BAA and the per-sub-account toggle are §9A's three conditions, and the
 * closing sentence is §9A's own warning about why the third one is not implied by the
 * first two.
 *
 * Only the clinic can see HighLevel's toggle, so only the clinic can attest to it. Until
 * it does, `CrmSyncService`'s gate 2 refuses every field that identifies a patient — not
 * even a name is sent — and the payload is never built.
 */
export const CRM_HIPAA_ATTESTATION =
  'I confirm that this clinic has HighLevel’s HIPAA add-on, that the in-app BAA is ' +
  'signed under Settings › Compliance, and that HIPAA mode is enabled on the ' +
  'sub-account we sync to.';

export const CRM_HIPAA_ATTESTATION_HINT =
  'Sub-accounts without the HIPAA toggle are not covered by HighLevel’s BAA. Until ' +
  'you confirm this, nothing that identifies a patient is sent — not even a name.';

/* ------------------------------------------------------------------- tags */

/**
 * `CrmTagSchema`, in the browser. A courtesy and never the enforcement point (rules
 * §17.2): the API applies the same grammar and then runs the scrubber, whose vocabulary
 * rules this deliberately does not copy — a screen that guessed at the clinical-term list
 * would tell a clinic a tag was fine and then watch the server refuse it.
 */
export const CRM_TAG_PATTERN = /^[a-z0-9][a-z0-9:_-]*$/;
export const CRM_TAG_MAX_LENGTH = 40;
export const CRM_TAGS_PER_EVENT = 8;

/** Why this tag cannot be added, or `undefined`. */
export function tagProblem(tag: string, existing: readonly string[]): string | undefined {
  if (tag === '') return 'Type a tag first.';
  if (existing.includes(tag)) return 'That tag is already on this event.';
  if (existing.length >= CRM_TAGS_PER_EVENT) {
    return `Use at most ${String(CRM_TAGS_PER_EVENT)} tags for one event.`;
  }
  if (tag.length > CRM_TAG_MAX_LENGTH) {
    return `Use at most ${String(CRM_TAG_MAX_LENGTH)} characters.`;
  }
  if (!CRM_TAG_PATTERN.test(tag)) {
    return 'Use lower-case letters, digits, "-", "_" or ":". No spaces.';
  }
  return undefined;
}

/* --------------------------------------------------------------- API issues */

/** Where a `VALIDATION_FAILED` issue landed, so the screen can put it on one control. */
export interface CrmFieldErrors {
  /** `tags[event][index]` — the message belongs on that chip. */
  tags: Partial<Record<CrmSyncEvent, Record<number, string>>>;
  /** Keyed by the CRM custom field whose source select caused it. */
  customFields: Partial<Record<CrmMappedField, string>>;
  /** Anything that named no control. Shown above the form rather than dropped. */
  form?: string;
}

const EVENTS = new Set<string>(Object.keys(CRM_EVENT_LABELS));
const MAPPED = new Set<string>(Object.keys(CRM_MAPPED_FIELD_LABELS));

/**
 * `details.issues[].path` onto the control that caused it (`API.md` §2.10).
 *
 * The two shapes the API sends are `['tags', <event>, <index>]` and
 * `['customFields', <field>]`. The index arrives as a string from the scrubber and as a
 * number from Zod's own parse, so both are read; an issue that matches neither shape is
 * returned as `form` rather than lost, because a message nobody sees is a save that
 * failed for no visible reason.
 */
export function crmIssuesToErrors(
  issues: readonly { path: (string | number)[]; message: string }[],
): CrmFieldErrors {
  const errors: CrmFieldErrors = { tags: {}, customFields: {} };

  for (const issue of issues) {
    const [head, second, third] = issue.path;

    if (head === 'tags' && typeof second === 'string' && EVENTS.has(second)) {
      const event = second as CrmSyncEvent;
      const index = Number(third);
      if (Number.isInteger(index) && index >= 0) {
        const forEvent = (errors.tags[event] ??= {});
        forEvent[index] ??= issue.message;
        continue;
      }
      /* `['tags', <event>]` with no index: the whole list is too long. */
      errors.form ??= `${CRM_EVENT_LABELS[event]}: ${issue.message}`;
      continue;
    }

    if (head === 'customFields' && typeof second === 'string' && MAPPED.has(second)) {
      const field = second as CrmMappedField;
      errors.customFields[field] ??= issue.message;
      continue;
    }

    errors.form ??= issue.message;
  }

  return errors;
}

/** True when nothing landed on a control, so the caller falls back to the API's message. */
export function hasNoFieldErrors(errors: CrmFieldErrors): boolean {
  return Object.keys(errors.tags).length === 0 && Object.keys(errors.customFields).length === 0;
}
